/**
 * CompetitiveTable - Tabla compacta tipo competitivo
 * Muestra: Sprite, Nombre, Tipos, Habilidades, Tier, Stats
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart } from 'lucide-react';
import { usePokemon } from '../../hooks/usePokemon';
import { useApp } from '../../context/AppContext';
import { TypeBadge } from './TypeBadge';
import { Skeleton } from '../ui/skeleton';
import { formatPokemonName } from '../../data/pokemonTranslations';
import { getMegaSpriteUrl } from '../../data/megaEvolutions';

// Competitive tiers based on base stat total (simplified)
const getTier = (bst) => {
  if (bst >= 600) return { name: 'Uber', color: 'text-red-500 bg-red-500/10' };
  if (bst >= 550) return { name: 'OU', color: 'text-orange-500 bg-orange-500/10' };
  if (bst >= 500) return { name: 'UU', color: 'text-yellow-500 bg-yellow-500/10' };
  if (bst >= 450) return { name: 'RU', color: 'text-green-500 bg-green-500/10' };
  if (bst >= 400) return { name: 'NU', color: 'text-blue-500 bg-blue-500/10' };
  if (bst >= 350) return { name: 'PU', color: 'text-purple-500 bg-purple-500/10' };
  if (bst >= 300) return { name: 'ZU', color: 'text-gray-500 bg-gray-500/10' };
  return { name: 'LC', color: 'text-pink-500 bg-pink-500/10' };
};

// Stat colors
const getStatColor = (value) => {
  if (value >= 150) return 'text-green-600 dark:text-green-400 font-bold';
  if (value >= 100) return 'text-lime-600 dark:text-lime-400';
  if (value >= 80) return 'text-yellow-600 dark:text-yellow-400';
  if (value >= 60) return 'text-orange-500';
  return 'text-red-500';
};

function CompetitiveRow({ pokemon, index }) {
  const { isFavorite, toggleFavorite, language } = useApp();
  const { getPokemon, getPokemonSpecies, getSprites, getLocalizedName } = usePokemon();
  const [data, setData] = useState(null);
  const [species, setSpecies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  useEffect(() => {
    let mounted = true;
    
    const fetchData = async () => {
      setLoading(true);
      const [pokemonData, speciesData] = await Promise.all([
        getPokemon(pokemon.id),
        getPokemonSpecies(pokemon.id)
      ]);
      if (mounted) {
        setData(pokemonData);
        setSpecies(speciesData);
        setLoading(false);
      }
    };
    
    fetchData();
    return () => { mounted = false; };
  }, [pokemon.id, getPokemon, getPokemonSpecies]);
  
  if (loading) {
    return (
      <tr className="border-b border-border">
        <td className="py-2 px-2"><Skeleton className="w-8 h-8 rounded" /></td>
        <td className="py-2 px-2"><Skeleton className="h-4 w-24" /></td>
        <td className="py-2 px-2"><Skeleton className="h-5 w-20" /></td>
        <td className="py-2 px-2"><Skeleton className="h-4 w-20" /></td>
        <td className="py-2 px-2"><Skeleton className="h-4 w-16" /></td>
        <td className="py-2 px-2"><Skeleton className="h-4 w-10" /></td>
        <td className="py-2 px-2 text-center"><Skeleton className="h-4 w-6 mx-auto" /></td>
        <td className="py-2 px-2 text-center"><Skeleton className="h-4 w-6 mx-auto" /></td>
        <td className="py-2 px-2 text-center"><Skeleton className="h-4 w-6 mx-auto" /></td>
        <td className="py-2 px-2 text-center"><Skeleton className="h-4 w-6 mx-auto" /></td>
        <td className="py-2 px-2 text-center"><Skeleton className="h-4 w-6 mx-auto" /></td>
        <td className="py-2 px-2 text-center"><Skeleton className="h-4 w-6 mx-auto" /></td>
      </tr>
    );
  }
  
  if (!data) return null;
  
  const sprites = getSprites(data);
  const types = data.types?.map(t => t.type.name) || [];
  const stats = data.stats || [];
  const bst = stats.reduce((sum, s) => sum + s.base_stat, 0);
  const tier = getTier(bst);
  const favorite = isFavorite(data.id);
  
  // Get abilities
  const normalAbilities = data.abilities?.filter(a => !a.is_hidden) || [];
  const hiddenAbility = data.abilities?.find(a => a.is_hidden);
  
  // Get localized name — fall back to our translations map when species data is unavailable
  const localizedName = species
    ? getLocalizedName(species.names)
    : formatPokemonName(data.name, language);
  
  // Stat mapping
  const statMap = {
    hp: stats.find(s => s.stat.name === 'hp')?.base_stat || 0,
    atk: stats.find(s => s.stat.name === 'attack')?.base_stat || 0,
    def: stats.find(s => s.stat.name === 'defense')?.base_stat || 0,
    spa: stats.find(s => s.stat.name === 'special-attack')?.base_stat || 0,
    spd: stats.find(s => s.stat.name === 'special-defense')?.base_stat || 0,
    spe: stats.find(s => s.stat.name === 'speed')?.base_stat || 0,
  };
  
  return (
    <motion.tr
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: index * 0.02 }}
      className="border-b border-border hover:bg-muted/50 transition-colors group"
      data-testid={`competitive-row-${data.id}`}
    >
      {/* Sprite */}
      <td className="py-1.5 px-2">
        <Link to={`/pokemon/${data.id}`} className="block relative">
          {!imageLoaded && <Skeleton className="w-10 h-10 rounded" />}
          <img
            src={sprites?.pixel || sprites?.default}
            alt={data.name}
            loading="lazy"
            onLoad={() => setImageLoaded(true)}
            className={`w-10 h-10 object-contain ${imageLoaded ? 'opacity-100' : 'opacity-0 absolute'}`}
          />
        </Link>
      </td>
      
      {/* Name */}
      <td className="py-1.5 px-2">
        <Link 
          to={`/pokemon/${data.id}`}
          className="font-medium text-foreground hover:text-violet-600 dark:hover:text-violet-400 transition-colors text-sm"
        >
          {localizedName}
        </Link>
      </td>
      
      {/* Types */}
      <td className="py-1.5 px-2">
        <div className="flex gap-1">
          {types.map(type => (
            <TypeBadge key={type} type={type} size="xs" showLabel={false} />
          ))}
        </div>
      </td>
      
      {/* Normal Abilities */}
      <td className="py-1.5 px-2">
        <div className="text-xs text-muted-foreground space-y-0.5">
          {normalAbilities.slice(0, 2).map((ability, i) => (
            <div key={i} className="capitalize truncate max-w-[100px]">
              {ability.ability.name.replace(/-/g, ' ')}
            </div>
          ))}
        </div>
      </td>
      
      {/* Hidden Ability */}
      <td className="py-1.5 px-2">
        {hiddenAbility && (
          <span className="text-xs text-violet-600 dark:text-violet-400 capitalize truncate block max-w-[80px]">
            {hiddenAbility.ability.name.replace(/-/g, ' ')}
          </span>
        )}
      </td>
      
      {/* Tier */}
      <td className="py-1.5 px-2 text-center">
        <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${tier.color}`}>
          {tier.name}
        </span>
      </td>
      
      {/* Stats */}
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.hp)}`}>
        <span className="block text-[10px] text-muted-foreground/60">HP</span>
        {statMap.hp}
      </td>
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.atk)}`}>
        <span className="block text-[10px] text-muted-foreground/60">Atk</span>
        {statMap.atk}
      </td>
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.def)}`}>
        <span className="block text-[10px] text-muted-foreground/60">Def</span>
        {statMap.def}
      </td>
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.spa)}`}>
        <span className="block text-[10px] text-muted-foreground/60">SpA</span>
        {statMap.spa}
      </td>
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.spd)}`}>
        <span className="block text-[10px] text-muted-foreground/60">SpD</span>
        {statMap.spd}
      </td>
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.spe)}`}>
        <span className="block text-[10px] text-muted-foreground/60">Spe</span>
        {statMap.spe}
      </td>
      
      {/* Favorite */}
      <td className="py-1.5 px-2">
        <button
          onClick={() => toggleFavorite(data.id)}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1"
          data-testid={`competitive-fav-${data.id}`}
        >
          <Heart className={`w-4 h-4 ${favorite ? 'fill-rose-500 text-rose-500' : 'text-muted-foreground'}`} />
        </button>
      </td>
    </motion.tr>
  );
}

// Row for Mega Evolutions (uses local data, no API fetch)
function MegaCompetitiveRow({ pokemon, index }) {
  const { language } = useApp();
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const types = pokemon.types || [];
  const stats = pokemon.stats || {};
  const bst = pokemon.bst || 0;
  const tier = getTier(bst);
  const spriteUrl = pokemon.spriteUrl || getMegaSpriteUrl(pokemon);
  const displayName = pokemon.displayName || pokemon.name;
  
  const statMap = {
    hp: stats.hp || 0,
    atk: stats.attack || 0,
    def: stats.defense || 0,
    spa: stats.spAtk || 0,
    spd: stats.spDef || 0,
    spe: stats.speed || 0,
  };
  
  return (
    <motion.tr
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: index * 0.02 }}
      className="border-b border-border hover:bg-yellow-500/5 transition-colors group"
      data-testid={`competitive-row-mega-${pokemon.id}`}
    >
      {/* Sprite */}
      <td className="py-1.5 px-2">
        <Link to={`/mega/${pokemon.id}`} className="block relative">
          {!imageLoaded && <Skeleton className="w-10 h-10 rounded" />}
          <img
            src={spriteUrl}
            alt={displayName}
            loading="lazy"
            onLoad={() => setImageLoaded(true)}
            onError={(e) => {
              e.target.src = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${pokemon.baseId}.png`;
            }}
            className={`w-10 h-10 object-contain ${imageLoaded ? 'opacity-100' : 'opacity-0 absolute'}`}
          />
        </Link>
      </td>
      
      {/* Name */}
      <td className="py-1.5 px-2">
        <Link 
          to={`/mega/${pokemon.id}`}
          className="font-medium text-foreground hover:text-yellow-600 dark:hover:text-yellow-400 transition-colors text-sm"
        >
          {displayName}
          <span className="ml-1 text-yellow-500 text-[10px]">✦</span>
        </Link>
      </td>
      
      {/* Types */}
      <td className="py-1.5 px-2">
        <div className="flex gap-1">
          {types.map(type => (
            <TypeBadge key={type} type={type} size="xs" showLabel={false} />
          ))}
        </div>
      </td>
      
      {/* Ability */}
      <td className="py-1.5 px-2">
        <div className="text-xs text-muted-foreground">
          <div className="capitalize truncate max-w-[100px]">
            {pokemon.ability || '-'}
          </div>
        </div>
      </td>
      
      {/* Hidden Ability (megas don't have one) */}
      <td className="py-1.5 px-2"></td>
      
      {/* Tier */}
      <td className="py-1.5 px-2 text-center">
        <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${tier.color}`}>
          {tier.name}
        </span>
      </td>
      
      {/* Stats */}
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.hp)}`}>
        <span className="block text-[10px] text-muted-foreground/60">HP</span>
        {statMap.hp}
      </td>
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.atk)}`}>
        <span className="block text-[10px] text-muted-foreground/60">Atk</span>
        {statMap.atk}
      </td>
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.def)}`}>
        <span className="block text-[10px] text-muted-foreground/60">Def</span>
        {statMap.def}
      </td>
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.spa)}`}>
        <span className="block text-[10px] text-muted-foreground/60">SpA</span>
        {statMap.spa}
      </td>
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.spd)}`}>
        <span className="block text-[10px] text-muted-foreground/60">SpD</span>
        {statMap.spd}
      </td>
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.spe)}`}>
        <span className="block text-[10px] text-muted-foreground/60">Spe</span>
        {statMap.spe}
      </td>
      
      {/* Favorite (empty for megas) */}
      <td className="py-1.5 px-2"></td>
    </motion.tr>
  );
}

// Row for Regional Forms (uses local data + API fetch for base species)
function RegionalCompetitiveRow({ pokemon, index }) {
  const { language } = useApp();
  const { getPokemon, getSprites } = usePokemon();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  useEffect(() => {
    let mounted = true;
    
    const fetchData = async () => {
      setLoading(true);
      // Regional forms have an apiId that matches PokeAPI
      if (pokemon.apiId) {
        const pokemonData = await getPokemon(pokemon.apiId);
        if (mounted && pokemonData) {
          setData(pokemonData);
        }
      }
      if (mounted) setLoading(false);
    };
    
    fetchData();
    return () => { mounted = false; };
  }, [pokemon.apiId, getPokemon]);
  
  if (loading) {
    return (
      <tr className="border-b border-border">
        <td className="py-2 px-2"><Skeleton className="w-8 h-8 rounded" /></td>
        <td className="py-2 px-2"><Skeleton className="h-4 w-24" /></td>
        <td className="py-2 px-2"><Skeleton className="h-5 w-20" /></td>
        <td className="py-2 px-2"><Skeleton className="h-4 w-20" /></td>
        <td className="py-2 px-2"><Skeleton className="h-4 w-16" /></td>
        <td className="py-2 px-2"><Skeleton className="h-4 w-10" /></td>
        <td className="py-2 px-2 text-center"><Skeleton className="h-4 w-6 mx-auto" /></td>
        <td className="py-2 px-2 text-center"><Skeleton className="h-4 w-6 mx-auto" /></td>
        <td className="py-2 px-2 text-center"><Skeleton className="h-4 w-6 mx-auto" /></td>
        <td className="py-2 px-2 text-center"><Skeleton className="h-4 w-6 mx-auto" /></td>
        <td className="py-2 px-2 text-center"><Skeleton className="h-4 w-6 mx-auto" /></td>
        <td className="py-2 px-2 text-center"><Skeleton className="h-4 w-6 mx-auto" /></td>
      </tr>
    );
  }
  
  // Use local data for types, or API data if available
  const types = pokemon.types || [];
  const displayName = pokemon.displayName || pokemon.name;
  const spriteUrl = pokemon.spriteUrl || pokemon.sprite;
  
  // Use API data for stats if available, otherwise use local types only
  let statMap, bst;
  if (data) {
    const stats = data.stats || [];
    bst = stats.reduce((sum, s) => sum + s.base_stat, 0);
    statMap = {
      hp: stats.find(s => s.stat.name === 'hp')?.base_stat || 0,
      atk: stats.find(s => s.stat.name === 'attack')?.base_stat || 0,
      def: stats.find(s => s.stat.name === 'defense')?.base_stat || 0,
      spa: stats.find(s => s.stat.name === 'special-attack')?.base_stat || 0,
      spd: stats.find(s => s.stat.name === 'special-defense')?.base_stat || 0,
      spe: stats.find(s => s.stat.name === 'speed')?.base_stat || 0,
    };
  } else {
    statMap = { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 };
    bst = 0;
  }
  
  const tier = getTier(bst);
  const abilities = data?.abilities?.filter(a => !a.is_hidden) || [];
  const hiddenAbility = data?.abilities?.find(a => a.is_hidden);
  
  return (
    <motion.tr
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: index * 0.02 }}
      className="border-b border-border hover:bg-cyan-500/5 transition-colors group"
      data-testid={`competitive-row-regional-${pokemon.id}`}
    >
      {/* Sprite */}
      <td className="py-1.5 px-2">
        <Link to={`/regional/${pokemon.id}`} className="block relative">
          {!imageLoaded && <Skeleton className="w-10 h-10 rounded" />}
          <img
            src={spriteUrl}
            alt={displayName}
            loading="lazy"
            onLoad={() => setImageLoaded(true)}
            onError={(e) => {
              e.target.src = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${pokemon.baseId}.png`;
            }}
            className={`w-10 h-10 object-contain ${imageLoaded ? 'opacity-100' : 'opacity-0 absolute'}`}
          />
        </Link>
      </td>
      
      {/* Name */}
      <td className="py-1.5 px-2">
        <Link 
          to={`/regional/${pokemon.id}`}
          className="font-medium text-foreground hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors text-sm"
        >
          {displayName}
          <span className="ml-1 text-cyan-500 text-[10px]">●</span>
        </Link>
      </td>
      
      {/* Types */}
      <td className="py-1.5 px-2">
        <div className="flex gap-1">
          {types.map(type => (
            <TypeBadge key={type} type={type} size="xs" showLabel={false} />
          ))}
        </div>
      </td>
      
      {/* Abilities */}
      <td className="py-1.5 px-2">
        <div className="text-xs text-muted-foreground space-y-0.5">
          {abilities.slice(0, 2).map((ability, i) => (
            <div key={i} className="capitalize truncate max-w-[100px]">
              {ability.ability.name.replace(/-/g, ' ')}
            </div>
          ))}
        </div>
      </td>
      
      {/* Hidden Ability */}
      <td className="py-1.5 px-2">
        {hiddenAbility && (
          <span className="text-xs text-violet-600 dark:text-violet-400 capitalize truncate block max-w-[80px]">
            {hiddenAbility.ability.name.replace(/-/g, ' ')}
          </span>
        )}
      </td>
      
      {/* Tier */}
      <td className="py-1.5 px-2 text-center">
        <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${tier.color}`}>
          {tier.name}
        </span>
      </td>
      
      {/* Stats */}
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.hp)}`}>
        <span className="block text-[10px] text-muted-foreground/60">HP</span>
        {statMap.hp}
      </td>
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.atk)}`}>
        <span className="block text-[10px] text-muted-foreground/60">Atk</span>
        {statMap.atk}
      </td>
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.def)}`}>
        <span className="block text-[10px] text-muted-foreground/60">Def</span>
        {statMap.def}
      </td>
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.spa)}`}>
        <span className="block text-[10px] text-muted-foreground/60">SpA</span>
        {statMap.spa}
      </td>
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.spd)}`}>
        <span className="block text-[10px] text-muted-foreground/60">SpD</span>
        {statMap.spd}
      </td>
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.spe)}`}>
        <span className="block text-[10px] text-muted-foreground/60">Spe</span>
        {statMap.spe}
      </td>
      
      {/* Favorite (empty for regionals) */}
      <td className="py-1.5 px-2"></td>
    </motion.tr>
  );
}

// Row for Official Forms (uses local data for stats/types)
function FormCompetitiveRow({ pokemon, index }) {
  const { isFavorite, toggleFavorite } = useApp();
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const types = pokemon.types || [];
  const stats = pokemon.stats || {};
  const bst = pokemon.bst || 0;
  const tier = getTier(bst);
  const spriteUrl = pokemon.spriteUrl;
  const displayName = pokemon.displayName || pokemon.name;
  const favorite = isFavorite(pokemon.id);
  
  const statMap = {
    hp: stats.hp || 0,
    atk: stats.attack || 0,
    def: stats.defense || 0,
    spa: stats.spAtk || 0,
    spd: stats.spDef || 0,
    spe: stats.speed || 0,
  };
  
  return (
    <motion.tr
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: index * 0.02 }}
      className="border-b border-border hover:bg-indigo-500/5 transition-colors group"
      data-testid={`competitive-row-form-${pokemon.id}`}
    >
      {/* Sprite */}
      <td className="py-1.5 px-2">
        <Link to={`/pokemon/${pokemon.apiId || pokemon.id}`} className="block relative">
          {!imageLoaded && <Skeleton className="w-10 h-10 rounded" />}
          <img
            src={spriteUrl}
            alt={displayName}
            loading="lazy"
            onLoad={() => setImageLoaded(true)}
            onError={(e) => {
              e.target.src = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${pokemon.baseId}.png`;
            }}
            className={`w-10 h-10 object-contain ${imageLoaded ? 'opacity-100' : 'opacity-0 absolute'}`}
          />
        </Link>
      </td>
      
      {/* Name */}
      <td className="py-1.5 px-2">
        <Link 
          to={`/pokemon/${pokemon.apiId || pokemon.id}`}
          className="font-medium text-foreground hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors text-sm"
        >
          {displayName}
          <span className="ml-1 text-indigo-500 text-[10px]">●</span>
        </Link>
      </td>
      
      {/* Types */}
      <td className="py-1.5 px-2">
        <div className="flex gap-1">
          {types.map(type => (
            <TypeBadge key={type} type={type} size="xs" showLabel={false} />
          ))}
        </div>
      </td>
      
      {/* Ability */}
      <td className="py-1.5 px-2">
        <div className="text-xs text-muted-foreground">
          <div className="capitalize truncate max-w-[100px]">
            {pokemon.ability || '-'}
          </div>
        </div>
      </td>
      
      {/* Hidden Ability (none for forms) */}
      <td className="py-1.5 px-2"></td>
      
      {/* Tier */}
      <td className="py-1.5 px-2 text-center">
        <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${tier.color}`}>
          {tier.name}
        </span>
      </td>
      
      {/* Stats */}
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.hp)}`}>
        <span className="block text-[10px] text-muted-foreground/60">HP</span>
        {statMap.hp}
      </td>
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.atk)}`}>
        <span className="block text-[10px] text-muted-foreground/60">Atk</span>
        {statMap.atk}
      </td>
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.def)}`}>
        <span className="block text-[10px] text-muted-foreground/60">Def</span>
        {statMap.def}
      </td>
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.spa)}`}>
        <span className="block text-[10px] text-muted-foreground/60">SpA</span>
        {statMap.spa}
      </td>
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.spd)}`}>
        <span className="block text-[10px] text-muted-foreground/60">SpD</span>
        {statMap.spd}
      </td>
      <td className={`py-1.5 px-1 text-center text-xs ${getStatColor(statMap.spe)}`}>
        <span className="block text-[10px] text-muted-foreground/60">Spe</span>
        {statMap.spe}
      </td>
      
      {/* Favorite */}
      <td className="py-1.5 px-2">
        <button
          onClick={() => toggleFavorite(pokemon.id)}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1"
          data-testid={`competitive-fav-form-${pokemon.id}`}
        >
          <Heart className={`w-4 h-4 ${favorite ? 'fill-rose-500 text-rose-500' : 'text-muted-foreground'}`} />
        </button>
      </td>
    </motion.tr>
  );
}

export function CompetitiveTable({ pokemon, displayCount }) {
  const { t } = useApp();
  const displayedPokemon = pokemon.slice(0, displayCount);
  
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-card" data-testid="competitive-table">
      <table className="w-full text-left min-w-[800px]">
        <thead className="bg-muted/50 sticky top-0">
          <tr>
            <th className="py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground w-12"></th>
            <th className="py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('name')}</th>
            <th className="py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Tipos</th>
            <th className="py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('abilities')}</th>
            <th className="py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('hiddenAbility')}</th>
            <th className="py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center">Tier</th>
            <th className="py-2 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center w-10">HP</th>
            <th className="py-2 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center w-10">Atk</th>
            <th className="py-2 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center w-10">Def</th>
            <th className="py-2 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center w-10">SpA</th>
            <th className="py-2 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center w-10">SpD</th>
            <th className="py-2 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center w-10">Spe</th>
            <th className="py-2 px-2 w-8"></th>
          </tr>
        </thead>
        <tbody>
          {displayedPokemon.map((poke, index) => {
            if (poke.isMega) {
              return <MegaCompetitiveRow key={`mega-${poke.id}`} pokemon={poke} index={index} />;
            }
            if (poke.isRegional) {
              return <RegionalCompetitiveRow key={`regional-${poke.id}`} pokemon={poke} index={index} />;
            }
            if (poke.isAlternate) {
              return <FormCompetitiveRow key={`form-${poke.id}`} pokemon={poke} index={index} />;
            }
            return <CompetitiveRow key={poke.id} pokemon={poke} index={index} />;
          })}
        </tbody>
      </table>
    </div>
  );
}

export default CompetitiveTable;
